/**
 * 이상한 방 - 체인저의 손이 바깥과 나누는 모양 (타입 전용)
 *
 * 근거 문서: 로드맵 M4(`changerTool` - `input/`은 `game/`을 보지 않는다 · 뷰·이벤트의 모양을 좁게 다시 적는다)
 *
 * `changerTool.ts`에서 갈라져 나왔다(상태 머신 하나로 500줄을 넘겼다). 여기 있는 것은 셋이 함께 읽는 모양들이다 -
 * 바(`ui/changerBar.ts`)가 그리는 `ChangerBarView`, 접착층(`game/matchSessionChanger.ts`)이 구조적으로 맞추는
 * `ChangerDeskView`·`ChangerViewSnapshot`·`ChangerToolEvent`, 무대(`app/stageChanger.ts`)가 채우는 `ChangerToolDeps`.
 * 값은 `CHANGER_VERBS` 하나다(순서가 곧 화면의 버튼 순서라 뷰의 모양에 딸린 값이다). 규칙은 없다.
 *
 * `game/`의 `PlayerEvent`·`ClientView.desk`와 이 모양들이 맞는지는 접착층의 타입 대입이 컴파일에서 검사한다 -
 * 여기서 `game/`을 import 하는 순간 `input/`이 위층을 보게 된다(레이어 방향).
 */

import type { PaletteColor } from '../catalog/palette'
import type { CellRect } from '../core/floorPlan'
import type { Catalog } from '../core/room'
import {
  VERBS,
  type CatalogEntry,
  type CatalogId,
  type Cell3,
  type InstanceId,
  type ObjectInstance,
  type PaletteIndex,
  type PlayerId,
  type RoomBounds,
  type RoomConfig,
  type RoomState,
  type Rot,
  type Verb,
} from '../core/types'
import type { VerbRejection, VerbRequest } from '../core/verbs'
import type { ChangerLockReason } from './changerLocks'
import type { PickMode, PickResult } from './tapPicker'

export type { ChangerLockReason } from './changerLocks'

/* --- 상수 ------------------------------------------------------------------------ */

/** 팔레트의 동사 순서 = 정본 `core/types.VERBS`. 화면의 버튼 순서가 호스트가 개수를 세는 순서(`matchView.VERB_ORDER`)와 같은 배열이다. */
export const CHANGER_VERBS: readonly Verb[] = VERBS

/* --- 타입 ------------------------------------------------------------------------ */

export type ChangerVerbView = Readonly<{ verb: Verb; locked: ChangerLockReason | null; active: boolean }>

export type ChangerCatalogView = Readonly<{ id: CatalogId; colorHex: number; selected: boolean }>

export type ChangerColorView = Readonly<{ id: PaletteIndex; name: string; hex: number; selected: boolean; locked: boolean }>

export type ChangerToolMode = 'idle' | 'selected' | 'moving' | 'adding'

/** 도구의 모드 상태(선택한 물건 · 든 소품까지). 바에는 `kind`만 나간다(`ChangerBarView.mode`). */
export type ChangerModeState =
  | Readonly<{ kind: 'idle' }>
  | Readonly<{ kind: 'selected'; id: InstanceId }>
  | Readonly<{ kind: 'moving'; id: InstanceId }>
  | Readonly<{ kind: 'adding'; catalogId: CatalogId }>

/** 바(`ui/changerBar.ts`)가 그리는 것 전부. 바는 계산하지 않는다 - 잠금·개수·이름이 다 여기서 나간다. */
export type ChangerBarView = Readonly<{
  budget: number
  /** 호스트가 확정한 수 + 아직 답이 안 온 요청. 표시용이다(머리말). */
  used: number
  /** 남은 수(호스트의 `desk.budgetLeft`에서 답을 기다리는 요청을 뺀 것). 띠가 `budget - used`를 다시 세지 않게 여기서 나간다. */
  left: number
  mode: ChangerToolMode
  /** 고른 물건(또는 놓으려는 소품)의 **카탈로그 id**. 없으면 `null`. 이름은 띠가 `i18n/itemName.ts`로 찾는다(아래 `zoneId` 규칙과 같다). */
  subjectItemId: CatalogId | null
  verbs: readonly ChangerVerbView[]
  /** 생김 중에만. */
  catalog: readonly ChangerCatalogView[] | null
  /** 물건을 고른 중에만. */
  colors: readonly ChangerColorView[] | null
  /** 되돌릴 물건의 **카탈로그 id**. 되돌릴 것이 없으면 `null`(버튼 잠금). */
  undoItemId: CatalogId | null
  /**
   * 내 구획의 id(M11). 띠가 그 이름표를 세운다 - **이름이 아니라 id**인 이유는 `input/`이 사전을 보지 않기 때문이다
   * (`i18n/zoneName.ts`가 `room.<id>`의 유일한 조회처이고, 부르는 쪽은 `ui/changerBar.ts`다).
   * 구획이 없는 판이면 `null`이고 그때 이름표는 서지 않는다.
   */
  zoneId: string | null
}>

/**
 * 뷰에서 도구가 읽는 것. `ClientView.desk`의 좁은 사본이고 접착층이 옮겨 준다(`game/`을 보지 않기 위해).
 * `budgetLeft`는 호스트가 센 남은 예산이다 - 도구는 여기에 답을 기다리는 요청만 얹고 `budget - used`를 다시 세지 않는다.
 */
export type ChangerDeskView = Readonly<{ budget: number; used: number; budgetLeft: number; touched: readonly InstanceId[] }>

export type ChangerViewSnapshot = Readonly<{
  /** 지금 작업대를 만질 수 있는가(바꾸기 페이즈 · 아직 커밋 전). 호스트의 `self.canChange` 그대로다. 거짓이면 어떤 탭도 받지 않는다. */
  active: boolean
  desk: ChangerDeskView | null
  /**
   * 손댈 수 있는 사각형 = **내 구획**(M11 규칙 2). 호스트의 `ClientView.myZone.rect` 그대로이고, 도구는 이 값을
   * `core/verbs.changerVerbContext`에 그대로 넘겨 팔레트를 미리 잠근다 - 여기서 "내 구획인가"를 비교하지 않는다.
   * `null`이면 검사가 없다(구획이 없는 판). 타입은 `core/floorPlanModel.CellRect` **그것이다** - 같은 모양을 다시 적으면
   * 그 타입에 필드가 붙는 날 이쪽만 조용히 남는다(그 값은 `core/verbs.changerVerbContext`로 그대로 흘러 들어간다).
   */
  zoneRect: CellRect | null
  /**
   * 내 구획의 id. 띠의 이름표(`ChangerBarView.zoneId`)로 그대로 나간다 - 도구는 이 값을 판단에 쓰지 않는다
   * (판단하는 것은 `zoneRect` 하나이고 그 집행자는 `core/verbs`다).
   */
  zoneId: string | null
}>

/**
 * 다녀간 사람의 걸기(M7) - "N개는 못 찾는다". `game/match/matchTypes.ChangerWager`와 같은 모양이다(`input/`은 `game/`을 보지 않는다).
 * 값의 규칙(1 이상 · 변화 개수 이하)은 호스트의 `raise.normalizeWager`가 접는다 - 도구는 띠의 스테퍼 값을 그대로 싼다.
 */
export type ChangerToolWager = Readonly<{ unfoundAtLeast: number }>

/** 도구가 호스트에 보내는 것. `PlayerEvent`의 세 갈래와 같은 모양이다(접착층이 구조적으로 맞춘다). */
export type ChangerToolEvent =
  | Readonly<{ type: 'applyChange'; request: VerbRequest }>
  | Readonly<{ type: 'undoChange'; id: InstanceId }>
  | Readonly<{ type: 'commitChanges'; wager: ChangerToolWager | null }>

/**
 * 놓을 자리 미리보기(고스트). 초안에는 그리지 않고 **비추기만** 한다 - 손가락이 움직일 때마다 초안을 고치면
 * 호스트에 요청이 그만큼 나간다.
 *
 * `valid`는 `core/verbs`를 실제로 돌려 본 결과다(잠금과 같은 규약 - 여기서 경계·점유를 다시 비교하지 않는다).
 * 거짓이면 무대가 빨간 고스트를 세운다 - 놓기 전에 안 된다는 것을 아는 것이 4.3절의 "선택 불가"다.
 */
export type ChangerPreview = Readonly<{ cell: Cell3; catalogId: CatalogId; rot: Rot; valid: boolean }>

export type ChangerPick = (clientX: number, clientY: number, room: RoomState, mode: PickMode) => PickResult | null

export type ChangerToolDeps = Readonly<{
  catalog: Catalog
  config: RoomConfig
  bounds: RoomBounds
  palette: readonly PaletteColor[]
  /** 카탈로그 스트립을 고르는 시드. 방 시드를 그대로 준다 - 같은 방이면 같은 여덟이다. */
  seed: string
  /** 내 좌석. 새 소품의 id 접두사이자 낙관적 초안의 `placedBy`다. */
  me: PlayerId
  /** 바꾸기 진입 시점의 초안(호스트가 `rooms`로 보낸 것). 여기서부터 낙관적으로 그린다. */
  initialDraft: RoomState
  /** 원본. 되돌리기가 물건을 이 모습으로 되돌린다(`matchChange.onUndoChange`와 같은 길). */
  original: RoomState
  view: () => ChangerViewSnapshot
  pick: ChangerPick
  /** 요청 하나를 호스트로. 돌려주는 `cseq`로 `rejected`를 짝짓는다. */
  send: (event: ChangerToolEvent) => number
  /** 초안이 바뀌었다 - 씬에 올려라. */
  onDraft: (draft: RoomState) => void
  /** 바가 다시 그릴 때. 상태가 바뀔 때마다 한 번. */
  onView: (view: ChangerBarView) => void
  /** 자리 탭처럼 미리 잠글 수 없던 요청이 verbs에 걸렸다. 토스트는 바깥의 몫이다. */
  onLocalReject: (reason: VerbRejection) => void
  /**
   * 남의 구획 물건을 탭했다(M11). 거부가 아니라 **아무 일도 없었음**이라 `onLocalReject`와 갈라 둔다 -
   * 요청을 보낸 적이 없고 예산도 초안도 움직이지 않았으므로 `VerbRejection`으로 말하면 거짓말이 된다.
   * 없으면 조용히 무시한다.
   */
  onOutOfZone?: (() => void) | undefined
  /** 고른 물건이 바뀌었다(외곽선). 없으면 `undefined` 둘. */
  onSelectionChanged?: ((obj: ObjectInstance | undefined, entry: CatalogEntry | undefined) => void) | undefined
  /** 놓을 자리 미리보기가 바뀌었다. `null`이면 끈다(놓는 중이 아니거나 가리킨 곳이 자리가 아니다). */
  onPreview?: ((preview: ChangerPreview | null) => void) | undefined
}>

export type ChangerTool = Readonly<{
  tapAt: (clientX: number, clientY: number) => void
  /**
   * 손가락이 가리키는 곳을 미리 비춘다(`onPreview`). 놓는 중이 아니면 아무 일도 없다 - 부르는 쪽
   * (`app/inputLayer.ts`)이 페이즈나 모드를 다시 보지 않아도 되게.
   */
  aimAt: (clientX: number, clientY: number) => void
  /** 지금 자리를 고르는 중인가(생김 · 옮김). 참이면 드래그는 화면을 옮기는 대신 겨눈다. */
  isPlacing: () => boolean
  pickVerb: (verb: Verb) => void
  pickCatalog: (id: CatalogId) => void
  pickColor: (colorId: PaletteIndex) => void
  undo: () => void
  /**
   * [다 했다]. `wager`는 띠의 스테퍼가 고른 "못 찾을 개수"(M7)다 - 0이면 걸지 않은 것(`null`로 보낸다). 도구는 값을
   * 판단하지 않는다(상한·정수 여부는 호스트의 `normalizeWager`가 접는다).
   */
  commit: (wager?: number) => void
  /* --- 호스트의 답. 접착층(`matchSessionChanger.ts`)이 효과를 여기로 옮긴다. --- */
  /** `changerProgress` - 수용된 요청 하나의 신호(되돌리기도 같다). */
  onProgress: () => void
  /** `changeRejected{reason}` - 머리의 요청이 verbs에 걸렸다. */
  onRejected: (reason: VerbRejection) => void
  /** `rejected{cseq}` - 그 요청이 리듀서 게이트에 걸렸다(예산 · 페이즈). */
  onRequestDropped: (cseq: number) => void
  /** 새 뷰가 왔다 - 잠금·점을 다시 계산한다. */
  onView: () => void
  getDraft: () => RoomState
  getBarView: () => ChangerBarView
  dispose: () => void
}>
